import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Globe, MousePointerClick, Eye, Bot, RefreshCw, ExternalLink, AlertTriangle, TrendingUp, Loader2 } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { LAYOUT } from '@/design_tokens';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { formatDistanceToNow } from 'date-fns';

const RANGES = [
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '90d', label: '90d' },
];

const fmtNum = (n) => (n || 0).toLocaleString('en-US');
const fmtRel = (iso) => {
  if (!iso) return 'never';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return 'never'; }
};

/**
 * iter166 — Cross-widget Embed Analytics dashboard.
 * Consumes GET /admin/embed-analytics/overview + /widgets to render
 * platform-wide KPIs, timeseries, top widgets, top referrers, bot split,
 * and a zombie-widget list for cleanup.
 */
export default function EmbedAnalyticsAdminPage() {
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (opts = {}) => {
    const { signal } = opts;
    try {
      setLoading(true);
      const r = await api.get(`/admin/embed-analytics/overview?range=${range}`, { signal });
      setData(r.data);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      toast.error(e.response?.data?.detail || 'Failed to load embed analytics');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  const kpis = useMemo(() => {
    const t = data?.totals;
    if (!t) return [];
    return [
      { key: 'impressions', label: 'Impressions', value: t.impressions, Icon: Eye, testId: 'kpi-impressions' },
      { key: 'clicks', label: 'Clicks', value: t.clicks, Icon: MousePointerClick, testId: 'kpi-clicks' },
      { key: 'ctr', label: 'CTR (human)', value: `${t.ctr_percent}%`, Icon: TrendingUp, testId: 'kpi-ctr' },
      { key: 'uniq', label: 'Unique visitors', value: t.unique_visitors, Icon: Globe, testId: 'kpi-unique' },
    ];
  }, [data]);

  return (
    <div className={LAYOUT.wideContainer} data-testid="embed-analytics-admin-page">
      <PageHeader
        title="Embeds Analytics"
        subtitle="Platform-wide performance of every embeddable widget — impressions, clicks, referrers, bot traffic, and zombie widgets ready for cleanup."
        icon={Globe}
        testId="embed-analytics-header"
        actions={
          <div className="flex items-center gap-2" data-testid="ea-range-toggle">
            {RANGES.map((r) => (
              <Button
                key={r.key}
                size="sm"
                variant={range === r.key ? 'default' : 'outline'}
                onClick={() => setRange(r.key)}
                data-testid={`ea-range-${r.key}`}
              >
                {r.label}
              </Button>
            ))}
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ea-refresh">
              <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        }
      />

      {loading && !data ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : !data ? null : (
        <div className="space-y-6">
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {kpis.map(({ key, label, value, Icon, testId }) => (
              <Card key={key}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide">
                    <Icon className="h-3 w-3" /> {label}
                  </div>
                  <div className="text-3xl font-bold text-slate-100 mt-1" data-testid={testId}>
                    {typeof value === 'number' ? fmtNum(value) : value}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Bot split */}
          <Card data-testid="bot-split-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Bot className="h-4 w-4" /> Bot vs Human traffic</CardTitle>
              <CardDescription>
                Impression breakdown for the window. High bot % on public widgets is normal — search engine
                crawlers count as impressions but never click.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex h-6 rounded overflow-hidden border border-slate-700/60">
                <div
                  className="bg-emerald-500/60 flex items-center justify-center text-xs text-emerald-50"
                  style={{ width: `${Math.max(2, 100 - data.totals.bot_percent)}%` }}
                  data-testid="bot-split-human"
                >
                  {(100 - data.totals.bot_percent).toFixed(0)}% human
                </div>
                <div
                  className="bg-slate-500/60 flex items-center justify-center text-xs text-slate-100"
                  style={{ width: `${Math.max(2, data.totals.bot_percent)}%` }}
                  data-testid="bot-split-bot"
                >
                  {data.totals.bot_percent.toFixed(0)}% bot
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 text-xs text-slate-400">
                <div>Human: <span className="text-slate-200 font-semibold">{fmtNum(data.totals.human_impressions)}</span> impressions</div>
                <div>Bot: <span className="text-slate-200 font-semibold">{fmtNum(data.totals.bot_impressions)}</span> impressions</div>
              </div>
            </CardContent>
          </Card>

          {/* Timeseries */}
          <Card data-testid="ea-timeseries-card">
            <CardHeader>
              <CardTitle>Impressions &amp; Clicks (human traffic)</CardTitle>
              <CardDescription>Daily totals across every active widget on the platform.</CardDescription>
            </CardHeader>
            <CardContent>
              {(data.timeseries?.length || 0) === 0 ? (
                <div className="h-56 flex items-center justify-center text-sm text-slate-400" data-testid="ea-timeseries-empty">
                  No embed traffic yet in this window.
                </div>
              ) : (
                <div className="h-56" data-testid="ea-timeseries-chart">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <LineChart data={data.timeseries} margin={{ top: 5, right: 24, left: 0, bottom: 5 }}>
                      <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }}
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        minTickGap={30} />
                      <YAxis stroke="#64748b" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #1f2937', borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12, color: '#e2e8f0' }} />
                      <Line type="monotone" dataKey="impressions" stroke="#60a5fa" strokeWidth={2} dot={false} name="Impressions" />
                      <Line type="monotone" dataKey="clicks" stroke="#f87171" strokeWidth={2} dot={false} name="Clicks" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top widgets */}
          <Card data-testid="top-widgets-card">
            <CardHeader>
              <CardTitle>Top 10 widgets by impressions</CardTitle>
              <CardDescription>Human impressions only. Click column shows all human clicks in the window.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.top_widgets.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">No widget traffic in this range.</div>
              ) : (
                <div className="border border-slate-700/60 rounded-lg divide-y divide-slate-800">
                  {data.top_widgets.map((w) => (
                    <div key={w.widget_id} className="px-3 py-2 flex items-center justify-between" data-testid={`top-widget-${w.widget_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 truncate flex items-center gap-2">
                          {w.name}
                          {!w.is_active && <Badge variant="secondary" className="bg-slate-800 text-slate-400 text-[10px]">disabled</Badge>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">owner: {w.user_id}</div>
                      </div>
                      <div className="flex items-center gap-6 text-xs text-slate-300 shrink-0">
                        <span><Eye className="inline h-3 w-3 mr-1 text-blue-400" />{fmtNum(w.impressions)}</span>
                        <span><MousePointerClick className="inline h-3 w-3 mr-1 text-red-400" />{fmtNum(w.clicks)}</span>
                        <Badge variant="secondary" className="bg-slate-800 text-slate-200">CTR {w.ctr_percent}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top referrers */}
          <Card data-testid="top-referrers-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><ExternalLink className="h-4 w-4" /> Top referring domains</CardTitle>
              <CardDescription>Which external sites drove impressions on any of your widgets.</CardDescription>
            </CardHeader>
            <CardContent>
              {data.top_referrers.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">No referrer data yet in this range.</div>
              ) : (
                <div className="grid gap-2">
                  {data.top_referrers.map((r) => {
                    const pct = (r.impressions / Math.max(data.totals.human_impressions, 1)) * 100;
                    return (
                      <div key={r.host} className="flex items-center gap-3" data-testid={`referrer-${r.host}`}>
                        <div className="w-56 text-sm text-slate-200 truncate">{r.host}</div>
                        <div className="flex-1 h-4 bg-slate-800 rounded overflow-hidden">
                          <div className="h-full bg-blue-500/50" style={{ width: `${Math.max(2, pct)}%` }} />
                        </div>
                        <div className="w-20 text-right text-xs text-slate-300">{fmtNum(r.impressions)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Zombies */}
          <Card data-testid="zombie-widgets-card">
            <CardHeader className="flex-row items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  Zombie widgets (no impressions in 30d)
                </CardTitle>
                <CardDescription>
                  Widgets marked active but silent — prime candidates for cleanup or an owner nudge.
                </CardDescription>
              </div>
              <Badge variant="secondary" className="bg-amber-900/30 text-amber-200" data-testid="zombie-total">
                {data.zombie_count}
              </Badge>
            </CardHeader>
            <CardContent>
              {data.zombie_widgets.length === 0 ? (
                <div className="text-sm text-slate-400 py-4">No zombies. Every active widget is getting traffic. 🎉</div>
              ) : (
                <div className="border border-slate-700/60 rounded-lg divide-y divide-slate-800 max-h-64 overflow-y-auto">
                  {data.zombie_widgets.map((w) => (
                    <div key={w.id} className="px-3 py-2 flex items-center justify-between" data-testid={`zombie-row-${w.id}`}>
                      <div>
                        <div className="text-sm text-slate-100">{w.name}</div>
                        <div className="text-xs text-slate-500">Created {fmtRel(w.created_at)} · owner: {w.user_id}</div>
                      </div>
                      <Badge variant="secondary" className="bg-slate-800 text-slate-400">silent</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
